import React, { FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../components/nav/LoadingSpinner';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import ICONS from '../../constants/icons';
import routes from '../../constants/routes';

import useGetTopFormulasMetrics from '../../hooks/metrics/useGetTopFormulasMetrics';

import {
    METRIC_NAMES,
    TopFormulasMetricModel,
    topPerformingMetricsPercent,
} from '../../models/metrics.model';

import TopMetricBaseCard from './TopMetricBaseCard';

const TopPerformingMessages: FC<React.PropsWithChildren<unknown>> = () => {
    const [selectedMetric, setSelectedMetric] = useState<METRIC_NAMES>(
        METRIC_NAMES.APP_VISITED_,
    );

    const metricNameForParams = useMemo(() => {
        return topPerformingMetricsPercent.find((item) =>
            item.includes(selectedMetric as string),
        );
    }, [selectedMetric]);

    const { data: topFormulaData, isPending: isLoading } =
        useGetTopFormulasMetrics(metricNameForParams!);

    const sortedData = useMemo<TopFormulasMetricModel[]>(() => {
        return (topFormulaData ?? []).sort((a, b) => {
            const key = 'percent';
            return b[key]! - a[key]!;
        });
    }, [topFormulaData]);

    const hasData = useMemo<boolean>(
        () => !isLoading && (topFormulaData ?? []).length > 0,
        [isLoading, topFormulaData],
    );

    return (
        <TopMetricBaseCard
            selectedMetric={selectedMetric}
            setSelectedMetric={setSelectedMetric}
        >
            <Content className="hide-scroll-bar">
                {isLoading && <LoadingSpinner styles={{ height: '100px' }} />}
                {hasData ? (
                    sortedData.map((item) => (
                        <Wrapper key={item.formula_id}>
                            <DataText
                                target="_blank"
                                rel="noopener"
                                to={routes.formulaLive(item.formula_id)}
                            >
                                {item.formula_name}
                            </DataText>
                            <Text>{`${item.percent?.toFixed(2)}%`}</Text>
                        </Wrapper>
                    ))
                ) : !isLoading ? (
                    <EmptyStateContainer>
                        <EmptyState>
                            <ICONS.messages
                                width={40}
                                height={40}
                                color={aampeTheme.colors.neutral09}
                            />
                            <EmptyStateText>
                                Launch some messages to see your top performers
                            </EmptyStateText>
                        </EmptyState>
                    </EmptyStateContainer>
                ) : null}
            </Content>
        </TopMetricBaseCard>
    );
};

const EmptyStateContainer = styled.div({
    borderRadius: aampeTheme.borderRadius.medium,
    border: `1px solid ${aampeTheme.colors.interactiveWG03}`,
    background: aampeTheme.colors.backgroundWG02,
    height: '100%',
    padding: aampeTheme.spacing4(2),
});

const EmptyState = styled.div({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    rowGap: aampeTheme.spacing4(2),
    justifyContent: 'center',
    alignItems: 'center',
});

const EmptyStateText = styled.span({
    color: aampeTheme.colors.neutral09,
    fontWeight: aampeTheme.weights.light,
    textAlign: 'center',
});

const Content = styled.div(() => ({
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
    overflowY: 'auto',
}));

const Wrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: aampeTheme.colors.backgroundWG02,
    padding: aampeTheme.spacing4(3, 4),
    borderRadius: aampeTheme.borderRadius.medium,
});

const Text = styled.span({
    color: aampeTheme.colors.textAccessibleWG01,
});

const DataText = styled(Link)({
    width: '70%',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textDecoration: 'none',
    color: aampeTheme.colors.neutral09,
    fontWeight: aampeTheme.weights.heavy,
});

export default TopPerformingMessages;
